import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MUITable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import SearchIcon from '@mui/icons-material/Search';
import { ReactElement, useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';
import getButtonText from '~/utils/text/getButtonText';
import { UserStatus } from '~/services/api/actTeamMember/types';
import { ILocalAdmin } from '~/services/api/localAdmin/types';
import { IParams as IValsParams, Sorting } from '~/services/api/types';
import Status from '~/ui/components/common/Status';
import AutoSubmit from '~/ui/components/inputs/AutoSubmit';
import Input from '~/ui/components/inputs/Input';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';
import getAssignedTeamsCount from '~/ui/pages/Clinics/ViewClinic/utils/getAssignedTeamsCount';

import { red } from '~/ui/constants/colors';
import { CLINIC_EDIT_LOCAL_ADMIN, CLINIC_VIEW_LOCAL_ADMIN } from '~/ui/constants/paths';
import { multipleStatusesOptions, sortingOptions } from '~/ui/constants/sortingOptions';

import editDisabledIcon from '~/ui/assets/images/editDisabled.svg';
import editGreyIcon from '~/ui/assets/images/editGrey.svg';
import styles from './Table.module.scss';

type IParams = {
  id: string;
};

const defaultValues = {
  name: '',
  sorting: Sorting.ByName,
  status: 0,
};

interface IProps {
  setLocalAdmin: (localAdmin: ILocalAdmin) => void;
}

const Table = ({ setLocalAdmin }: IProps): ReactElement => {
  const { id } = useParams<IParams>();
  const navigate = useNavigate();
  const formMethods = useForm({ defaultValues });
  const {
    register,
    control,
    watch,
    formState: { errors },
    handleSubmit,
  } = formMethods;

  const watchValues = watch();

  const { list: localAdmins, pagination } = useStoreState(state => state.localAdmin);
  const showError = useStoreActions(actions => actions.snackbar.showError);
  const onGetLocalAdmins = useStoreActions(actions => actions.localAdmin.onGetLocalAdmins);

  const onMount = useCallback(async () => {
    try {
      await onGetLocalAdmins({ clinicId: id, params: { pageNumber: 1, pageSize: 5 } });
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  }, [id, onGetLocalAdmins, showError]);

  const onSubmit = (vals: IValsParams) => {
    const { pageSize } = pagination;

    onGetLocalAdmins({ clinicId: id, params: { pageSize, pageNumber: 1, ...vals } });
  };

  useEffect(() => {
    onMount();
  }, [onMount]);

  const handlePagination = (pageNumber: number, pageSize: number) => {
    onGetLocalAdmins({
      clinicId: id,
      params: { pageNumber, pageSize, ...watchValues },
    });
  };

  return (
    <TableContainer>
      <Box sx={{ p: 2 }}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={1}>
              <Grid
                size={{
                  sm: 4,
                }}
              >
                <Input
                  startAdornment={<SearchIcon color="primary" />}
                  placeholder="Search Local Admin"
                  register={register}
                  name="name"
                  errors={errors}
                />
              </Grid>
              <Grid
                size={{
                  sm: 2,
                }}
              >
                <Select
                  label="Sort By"
                  control={control}
                  name="sorting"
                  errors={errors}
                  options={sortingOptions}
                  color={red}
                  hideSelectedOptions={false}
                />
              </Grid>
              <Grid
                size={{
                  sm: 2,
                }}
              >
                <Select
                  label="Filter By Status"
                  control={control}
                  name="status"
                  errors={errors}
                  options={multipleStatusesOptions}
                  color={red}
                  hideSelectedOptions={false}
                />
              </Grid>
              <AutoSubmit debounce={1000} initialValues={defaultValues} onSubmit={onSubmit} />
            </Grid>
          </form>
        </FormProvider>
      </Box>
      <MUITable>
        <TableHead>
          <TableRow>
            <TableCell>First Name</TableCell>
            <TableCell>Last Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
            <TableCell />
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {localAdmins.map(localAdmin => (
            <TableRow key={localAdmin.id}>
              <TableCell
                className={styles.link}
                onClick={() => {
                  navigate(
                    CLINIC_VIEW_LOCAL_ADMIN.replace(':id', id).replace(
                      ':localAdminId',
                      String(localAdmin.id),
                    ),
                  );
                }}
              >
                {localAdmin.firstName}
              </TableCell>
              <TableCell>{localAdmin.lastName}</TableCell>
              <TableCell>{localAdmin.email}</TableCell>
              <TableCell>
                <Status status={localAdmin.status} />
              </TableCell>
              <TableCell>
                {[UserStatus.DEACTIVATED, UserStatus.LOCKED].includes(
                  localAdmin.status as UserStatus,
                ) ? (
                  <IconButton disabled size="large">
                    <img src={editDisabledIcon} alt="disabled" />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={() =>
                      navigate(
                        CLINIC_EDIT_LOCAL_ADMIN.replace(':id', id).replace(
                          ':localAdminId',
                          String(localAdmin.id),
                        ),
                      )
                    }
                    size="large"
                  >
                    <img src={editGreyIcon} alt="edit" />
                  </IconButton>
                )}
              </TableCell>
              <TableCell>
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={() => {
                    setLocalAdmin(localAdmin);
                  }}
                >
                  {getButtonText(localAdmin.status)}
                </button>
                {localAdmin.status === UserStatus.PENDING && (
                  <button
                    type="button"
                    className={styles.actionButton}
                    onClick={() =>
                      setLocalAdmin({
                        ...localAdmin,
                        status: 'VerifyingEmail',
                      })
                    }
                  >
                    Archive
                  </button>
                )}
              </TableCell>
              <TableCell>
                <button
                  type="button"
                  disabled={
                    localAdmin.status !== UserStatus.ACTIVE ||
                    getAssignedTeamsCount(localAdmin) <= 1
                  }
                  className={styles.actionButton}
                  onClick={() => setLocalAdmin({ ...localAdmin, status: 'Assigned' })}
                >
                  Unassign
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </MUITable>
      <TablePagination
        component="div"
        count={pagination.total}
        onPageChange={(_, pageNumber) => {
          handlePagination(pageNumber + 1, pagination.pageSize);
        }}
        onRowsPerPageChange={e => {
          handlePagination(1, Number(e.target.value));
        }}
        page={pagination.pageNumber - 1}
        rowsPerPage={pagination.pageSize}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </TableContainer>
  );
};

export default Table;
